import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ReactComponent as LinkIcon } from '$icons/uncolored/link.svg';
import { ReactComponent as Video } from '$icons/uncolored/video.svg';
import { ReactComponent as Microphone } from '$icons/uncolored/microphone.svg';
import { ReactComponent as ArrowDown } from '$icons/uncolored/arrow.svg';
import { ReactComponent as SmartInterviewIcon } from '$icons/MSInterview.svg';
import { ReactComponent as VerifiedIcon } from '$icons/uncolored/verified.svg';
import { ReactComponent as AssessmentIcon } from '$icons/uncolored/assessment.svg';
import getUserType from '$utils/getUserType';
import {
  CandidateAdditionalVideoType,
  CandidateAssessmentDetail,
  CandidateData,
  CandidateReferralData,
  CandidateSmartInterviewsVideos,
} from '$types/candidates';
import { ReferralStatus } from '$constants/ReferralStatus';
import { UserTypes } from '$types/UserTypes';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import SmartInterviewVideoModal from '$components/SmartInterviewVideoModal/SmartInterviewVideoModal';
import SpkProfileReferences from '$components/SpkProfileReferences/SpkProfileReferences';
import SPKShowcaseModalShell from '$components/SPKShowcaseModalShell/SPKShowcaseModalShell';
import { FileCategoryType } from '$types/categoryType';
import SPKToast from '$components/SPKToast/SPKToast';
import copyToClipboard from '$utils/copyToClipboard';
import isAudioFile from '$utils/isAudioFile';
import { useTranslation } from 'react-i18next';
import { tracker } from '$utils/tracker';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import { useGetCandidateAssessmentsQuery } from '$store/modules/candidate-assessments';

import SPKSingleEmployerCandidateShowcase from './SPKSingleEmployerCandidateShowcase/SPKSingleEmployerCandidateShowcase';
import SPKEmployerShowcaseVideoModal from './SPKEmployerShowcaseVideoModal/SPKEmployerShowcaseVideoModal';
import SPKEmployerShowcaseAudioModal from './SPKEmployerShowcaseAudioModal/SPKEmployerShowcaseAudioModal';
import SPKEmployerShowcaseAssessmentsModal from './SPKEmployerShowcaseAssessmentsModal/SPKEmployerShowcaseAssessmentsModal';
import SPKShowcaseCandidateInfo from './SPKShowcaseCandidateInfo/SPKShowcaseCandidateInfo';

const AUDIO_ASSESSMENT_TITLE = 'Audio Assessment';
const ASSESSMENTS_TITLE = 'Assessments';
const MAX_FILES_PER_CATEGORY = 5;

/** Repeated categories get numbered so employers can tell the entries apart */
const withFilePosition = (title: string, position: number, total: number) =>
  total > 1 ? `${title} ${position}` : title;

type SortedAttachmentType = {
  Icon: any;
  Title: string;
  Link?: string;
  BehanceFix?: boolean;
  onClick?: () => void;
  index?: number;
  segmentEvent?: () => void;
  copyVideoShareUrl?: () => void;
};

type ShowcaseFile = NonNullable<CandidateData['Files']>[number];

type ModalItem =
  | {
      type: 'video';
      title: string;
      data: CandidateAdditionalVideoType;
    }
  | {
      type: 'smartInterview';
      title: string;
      data: CandidateSmartInterviewsVideos;
    }
  | {
      type: 'audio';
      title: string;
      data: ShowcaseFile;
    }
  | {
      type: 'references';
      title: string;
      data: CandidateReferralData[];
    }
  | {
      type: 'assessments';
      title: string;
      data: CandidateAssessmentDetail[];
    };

const SPKEmployerCandidateShowcase = ({
  candidate,
  positionId,
}: {
  candidate: CandidateData;
  positionId: number;
}) => {
  const [t] = useTranslation('global');
  const [showMore, setShowMore] = useState(false);
  const [modalItems, setModalItems] = useState<ModalItem[]>([]);
  const [selectedModalIndex, setSelectedModalIndex] = useState<number | null>(
    null
  );
  const userType = getUserType();
  const { displayName } = useAppSelector((state) => state.brand);
  const DISPLAYED_FILE_CATEGORIES_ID = useMemo(() => {
    return [FileCategoryType.REFERENCE, FileCategoryType.WRITING_SAMPLES];
  }, []);
  const [sortedAttachments, setSortedAttachments] = useState<
    SortedAttachmentType[]
  >([]);

  const { data: candidateAssessments } = useGetCandidateAssessmentsQuery(
    { candidateId: candidate.Id },
    { skip: !candidate.Id }
  );

  /**
   * Files flagged as not visible for employers stay out of the showcase for
   * everyone but the internal team, which always sees the full list.
   */
  const showcaseFiles: ShowcaseFile[] = useMemo(() => {
    const files = ('Files' in candidate && candidate.Files) || [];
    if (userType === UserTypes.INTERNAL) return files;
    return files.filter(
      (singleFile) => singleFile.IsEmployersVisible !== false
    );
  }, [candidate, userType]);

  const DisplayedFilesAmmount = 4;

  const toggleItemsVisibilityHandler = () => {
    if (!showMore) {
      tracker.emit('Candidate Show More Clicked', {
        candidate_id: candidate.Id,
        position_id: positionId,
      });
    } else {
      tracker.emit('Candidate Show Less Clicked', {
        candidate_id: candidate.Id,
        position_id: positionId,
      });
    }
    setShowMore(!showMore);
  };

  const copyShareVideoUrlHandler = useCallback(
    async (ShareReferenceId: string) => {
      const baseUrl = window.location.origin;
      const shareVideoURL = `${baseUrl}/share/candidate/${candidate.Id}/video/${ShareReferenceId}`;
      await copyToClipboard(shareVideoURL);
      SPKToast(t('buttonShare.alertCopySuccess'));
    },
    [candidate.Id, t]
  );

  const openModalItem = useCallback(
    (index: number, item: ModalItem) => {
      setSelectedModalIndex(index);
      if (item.type === 'references') {
        tracker.emit('Open References Modal', {
          candidate_id: candidate.Id,
          position_id: positionId,
          references_count: item.data.length,
        });
      } else if (item.type === 'audio') {
        tracker.emit('Open Audio Assessment Modal', {
          candidate_id: candidate.Id,
          position_id: positionId,
          file_id: item.data.Id,
        });
      } else if (item.type === 'assessments') {
        tracker.emit('Open Assessments Modal', {
          candidate_id: candidate.Id,
          position_id: positionId,
          assessments_count: item.data.length,
        });
      } else {
        tracker.emit('Open Highlight Video Modal', {
          candidate_id: candidate.Id,
          position_id: positionId,
          title: item.title,
        });
      }
    },
    [candidate.Id, positionId]
  );

  useEffect(() => {
    let tempSortedAttachments: SortedAttachmentType[] = [];
    const tempModalItems: ModalItem[] = [];

    if ('ScorecardUrl' in candidate && candidate.ScorecardUrl)
      tempSortedAttachments.push({
        Icon: (
          <LinkIcon
            className={`${
              userType === UserTypes.INTERNAL
                ? 'fill-internal-main'
                : 'fill-employer-primary'
            }`}
          />
        ),
        Title: 'AI Scorecard (beta)',
        Link: candidate.ScorecardUrl,
        segmentEvent: () => {
          tracker.emit('AI Scorecard Link Clicked', {
            candidate_id: candidate.Id,
            position_id: positionId,
          });
        },
      });

    /** Videos */
    if ('AdditionalVideos' in candidate) {
      candidate.AdditionalVideos.forEach((item) => {
        const modalIndex = tempModalItems.length;
        const modalItem: ModalItem = {
          type: 'video',
          title: item.Name,
          data: item,
        };
        tempModalItems.push(modalItem);
        tempSortedAttachments.push({
          Icon: (
            <Video
              className={`${
                userType === UserTypes.INTERNAL
                  ? 'fill-internal-main'
                  : 'fill-employer-primary'
              }`}
            />
          ),
          Title: item.Name,
          onClick: () => openModalItem(modalIndex, modalItem),
          copyVideoShareUrl: () =>
            copyShareVideoUrlHandler(item.ShareReferenceId),
        });
      });
    }

    /** SmartInterviews */
    if ('SmartInterviewsVideos' in candidate) {
      candidate.SmartInterviewsVideos?.forEach((item) => {
        const modalIndex = tempModalItems.length;
        const modalItem: ModalItem = {
          type: 'smartInterview',
          title: item.SmartInterviewQuestions?.Name ?? '',
          data: item,
        };
        tempModalItems.push(modalItem);
        tempSortedAttachments.push({
          Icon: (
            <SmartInterviewIcon
              className={`h-[14px] w-[15px] ${
                userType === UserTypes.INTERNAL
                  ? 'stroke-internal-main'
                  : 'stroke-employer-primary'
              }`}
            />
          ),
          Title: item.SmartInterviewQuestions?.Name ?? '',
          onClick: () => openModalItem(modalIndex, modalItem),
        });
      });
    }

    /** Audio Files, stored under the "Audio Assessment" file category */
    const audioFiles = showcaseFiles.filter(isAudioFile);
    audioFiles.forEach((singleFile, index) => {
      const modalIndex = tempModalItems.length;
      const title = withFilePosition(
        AUDIO_ASSESSMENT_TITLE,
        index + 1,
        audioFiles.length
      );
      const modalItem: ModalItem = {
        type: 'audio',
        title,
        data: singleFile,
      };
      tempModalItems.push(modalItem);
      tempSortedAttachments.push({
        Icon: (
          <Microphone
            className={`h-[14px] w-[15px] ${
              userType === UserTypes.INTERNAL
                ? 'fill-internal-main'
                : 'fill-employer-primary'
            }`}
          />
        ),
        Title: title,
        onClick: () => openModalItem(modalIndex, modalItem),
      });
    });

    /**
     * Assessments always collapse into a single showcase entry, however many
     * scenarios came back; the modal is what lists them all.
     */
    if (candidateAssessments?.length) {
      const modalIndex = tempModalItems.length;
      const modalItem: ModalItem = {
        type: 'assessments',
        title: ASSESSMENTS_TITLE,
        data: candidateAssessments,
      };
      tempModalItems.push(modalItem);
      tempSortedAttachments.push({
        Icon: (
          <AssessmentIcon
            className={`h-[14px] w-[15px] ${
              userType === UserTypes.INTERNAL
                ? 'stroke-internal-main'
                : 'stroke-employer-primary'
            }`}
          />
        ),
        Title: ASSESSMENTS_TITLE,
        onClick: () => openModalItem(modalIndex, modalItem),
      });
    }

    /** References */
    if ('CandidateReferrals' in candidate) {
      const displayableReferences = (candidate.CandidateReferrals || []).filter(
        (ref) => ref.Status === ReferralStatus.COMPLETED && ref.WouldHireAgain
      );

      if (displayableReferences.length > 0) {
        const modalIndex = tempModalItems.length;
        const modalItem: ModalItem = {
          type: 'references',
          title: 'References',
          data: displayableReferences,
        };
        tempModalItems.push(modalItem);
        tempSortedAttachments.push({
          Icon: (
            <VerifiedIcon
              className={`h-[14px] w-[15px] ${
                userType === UserTypes.INTERNAL
                  ? 'fill-internal-main'
                  : 'fill-employer-primary'
              }`}
            />
          ),
          Title: `Reference${displayableReferences.length > 1 ? 's' : ''} [${
            displayableReferences.length
          }]`,
          onClick: () => openModalItem(modalIndex, modalItem),
        });
      }
    }

    /** Custom Url */
    if ('CustomUrl' in candidate && candidate.CustomUrl) {
      tempSortedAttachments.push({
        Icon: (
          <LinkIcon
            className={`${
              userType === UserTypes.INTERNAL
                ? 'fill-internal-main'
                : 'fill-employer-primary'
            }`}
          />
        ),
        Title: `${candidate.User?.FirstName}'s Portfolio`,
        Link: candidate.CustomUrl,
        segmentEvent: () => {
          tracker.emit('Candidate External Link Clicked', {
            candidate_id: candidate.Id,
            target_url: candidate.CustomUrl,
            target: 'Custom',
            position_id: positionId,
          });
        },
      });
    }

    /** Behance Url */
    if ('BehanceUrl' in candidate && candidate.BehanceUrl) {
      tempSortedAttachments.push({
        Icon: (
          <LinkIcon
            className={`${
              userType === UserTypes.INTERNAL
                ? 'fill-internal-main'
                : 'fill-employer-primary'
            }`}
          />
        ),
        Title: `${candidate.User?.FirstName}'s Behance Portfolio`,
        Link: candidate.BehanceUrl,
        BehanceFix: true,
        segmentEvent: () => {
          tracker.emit('Candidate External Link Clicked', {
            candidate_id: candidate.Id,
            target_url: candidate.BehanceUrl,
            target: 'Behance',
            position_id: positionId,
          });
        },
      });
    }

    /** Github Url */
    if ('GithubUrl' in candidate && candidate.GithubUrl) {
      tempSortedAttachments.push({
        Icon: (
          <LinkIcon
            className={`${
              userType === UserTypes.INTERNAL
                ? 'fill-internal-main'
                : 'fill-employer-primary'
            }`}
          />
        ),
        Title: `${candidate.User?.FirstName}'s Github`,
        Link: candidate.GithubUrl,
        segmentEvent: () => {
          tracker.emit('Candidate External Link Clicked', {
            candidate_id: candidate.Id,
            target_url: candidate.GithubUrl,
            target: 'Github',
            position_id: positionId,
          });
        },
      });
    }

    // Audio Files are shown above, so they must not take up the document slots
    const documentFiles = showcaseFiles.filter(
      (singleFile) => !isAudioFile(singleFile)
    );
    /**
     * Grouped per category so every displayed category contributes up to
     * MAX_FILES_PER_CATEGORY entries of its own.
     */
    const displayedDocumentFiles = DISPLAYED_FILE_CATEGORIES_ID.flatMap(
      (categoryId) => {
        const categoryFiles = documentFiles
          .filter((singleFile) => singleFile.FileCategory?.Id === categoryId)
          .slice(0, MAX_FILES_PER_CATEGORY);
        return categoryFiles.map((singleFile, index) => ({
          singleFile,
          position: index + 1,
          total: categoryFiles.length,
        }));
      }
    );

    displayedDocumentFiles.forEach(({ singleFile, position, total }) => {
      const isPdf = singleFile.Url?.includes('.pdf') ?? false;
      const pdfViewerUrl = new URL(window.origin);
      pdfViewerUrl.pathname = `file-viewer/file/${candidate.Id}/${singleFile.Id}`;
      tempSortedAttachments.push({
        Icon: (
          <LinkIcon
            className={`${
              userType === UserTypes.INTERNAL
                ? 'fill-internal-main'
                : 'fill-employer-primary'
            }`}
          />
        ),
        Title: withFilePosition(
          `${isPdf ? 'View' : 'Download'} ${
            singleFile.FileCategory?.CategoryName ?? ''
          }`,
          position,
          total
        ),
        Link: isPdf ? pdfViewerUrl.toString() : singleFile.Url,
        segmentEvent: () => {
          if (singleFile?.FileCategory?.Id === FileCategoryType.REFERENCE) {
            tracker.emit('Reference Download', {
              candidate_id: candidate.Id,
              file_id: singleFile.Id,
            });
          } else {
            tracker.emit('Write Assessment Download', {
              candidate_id: candidate.Id,
              file_id: singleFile.Id,
            });
          }
        },
      });
    });

    /** Curriculum File */
    if ('Curriculum' in candidate && candidate?.Curriculum?.Url) {
      const cvUrl = new URL(window.origin);
      cvUrl.pathname = `file-viewer/cv/${candidate.Id}`;
      tempSortedAttachments.push({
        Icon: (
          <LinkIcon
            className={`${
              userType === UserTypes.INTERNAL
                ? 'fill-internal-main'
                : 'fill-employer-primary'
            }`}
          />
        ),
        Title: `View ${candidate.User?.FirstName} ${candidate.User?.LastName} CV`,
        Link: cvUrl.toString(),
        segmentEvent: () => {
          tracker.emit('CV Downloaded', {
            candidate_id: candidate.Id,
            file_id: candidate?.Curriculum?.Id,
            position_id: positionId,
          });
        },
      });
    }

    tempSortedAttachments = tempSortedAttachments.map((item, index) => {
      item.index = index;
      return item;
    });

    setSortedAttachments(tempSortedAttachments);
    setModalItems(tempModalItems);
  }, [
    candidate,
    showcaseFiles,
    candidateAssessments,
    DISPLAYED_FILE_CATEGORIES_ID,
    positionId,
    userType,
    copyShareVideoUrlHandler,
    openModalItem,
  ]);

  const selectedModalItem =
    selectedModalIndex !== null ? modalItems[selectedModalIndex] : undefined;
  const hasPrevious = selectedModalIndex !== null && selectedModalIndex > 0;
  const hasNext =
    selectedModalIndex !== null && selectedModalIndex < modalItems.length - 1;
  const closeModal = () => setSelectedModalIndex(null);

  const goToPrevious = () => {
    if (selectedModalIndex === null) return;
    const newIndex = selectedModalIndex - 1;
    const item = modalItems[newIndex];
    if (item) openModalItem(newIndex, item);
  };

  const goToNext = () => {
    if (selectedModalIndex === null) return;
    const newIndex = selectedModalIndex + 1;
    const item = modalItems[newIndex];
    if (item) openModalItem(newIndex, item);
  };

  return (
    <>
      {selectedModalItem && (
        <ModalInTailwind isOpen={!!selectedModalItem} closeModal={closeModal}>
          <SPKShowcaseModalShell
            title={`${displayName} Professional Key`}
            onClose={closeModal}
            onPrevious={goToPrevious}
            onNext={goToNext}
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            showNavigation={modalItems.length > 1}
          >
            {selectedModalItem.type === 'video' && (
              <SPKEmployerShowcaseVideoModal
                selectedVideo={selectedModalItem.data}
                setSelectedVideo={closeModal}
                candidate={candidate}
              />
            )}
            {selectedModalItem.type === 'smartInterview' && (
              <div className="flex flex-col overflow-auto bg-white py-5 px-4 rounded-2xl w-full">
                <SPKShowcaseCandidateInfo candidate={candidate} />
                <SmartInterviewVideoModal video={selectedModalItem.data} />
              </div>
            )}
            {selectedModalItem.type === 'audio' && (
              <SPKEmployerShowcaseAudioModal
                selectedAudio={selectedModalItem.data}
                title={selectedModalItem.title}
                candidate={candidate}
              />
            )}
            {selectedModalItem.type === 'assessments' && (
              <SPKEmployerShowcaseAssessmentsModal
                assessments={selectedModalItem.data}
                title={selectedModalItem.title}
                candidate={candidate}
              />
            )}
            {selectedModalItem.type === 'references' && (
              <div className="flex flex-col overflow-auto bg-white py-5 px-4 rounded-2xl w-full">
                <SPKShowcaseCandidateInfo candidate={candidate} />
                <div className="mb-4 text-spk-highContrast text-base font-bold">
                  References
                </div>
                <SpkProfileReferences references={selectedModalItem.data} />
              </div>
            )}
          </SPKShowcaseModalShell>
        </ModalInTailwind>
      )}
      <div className="flex flex-col gap-2">
        {sortedAttachments.map((singleAttachment, index) => {
          if (index < DisplayedFilesAmmount)
            return (
              <SPKSingleEmployerCandidateShowcase
                icon={singleAttachment.Icon}
                title={singleAttachment.Title}
                link={singleAttachment.Link}
                onClick={singleAttachment.onClick}
                key={`${candidate.Id}_attachment_${singleAttachment.index}`}
                segmentEvent={singleAttachment.segmentEvent}
                onCopyVideoShareUrl={singleAttachment.copyVideoShareUrl}
              />
            );
          return (
            <SPKSingleEmployerCandidateShowcase
              additionalClassNames={!showMore ? 'hidden' : 'flex'}
              icon={singleAttachment.Icon}
              title={singleAttachment.Title}
              link={singleAttachment.Link}
              onClick={singleAttachment.onClick}
              key={`${candidate.Id}_attachment_${singleAttachment.index}`}
              segmentEvent={singleAttachment.segmentEvent}
              onCopyVideoShareUrl={singleAttachment.copyVideoShareUrl}
            />
          );
        })}
      </div>
      {DisplayedFilesAmmount < sortedAttachments.length && (
        <button
          type="button"
          onClick={toggleItemsVisibilityHandler}
          className={`flex flex-row items-center gap-2 text-xs ${
            userType === UserTypes.INTERNAL
              ? 'text-internal-main'
              : 'text-employer-primary'
          } font-bold mt-2`}
        >
          <ArrowDown
            className={`w-3 h-3 ${
              userType === UserTypes.INTERNAL
                ? 'fill-internal-main'
                : 'fill-employer-primary'
            } ${showMore ? 'rotate-180' : 'rotate-0'}`}
          />
          Show {!showMore ? ' More' : ' Less'}
        </button>
      )}
    </>
  );
};

SPKEmployerCandidateShowcase.defaultProps = {
  role: UserTypes.EMPLOYER,
};

export default memo(SPKEmployerCandidateShowcase);
